import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Button, Paragraph, Spinner, XStack, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import { useDebugParseShoppingListImage, type OcrModelId } from '@mrrmlab/feature-shopping-list';
import { ocrImageStore } from '../ocr-image-store';
import { ocrAnalyzeStore } from '../ocr-analyze-store';

// Draws file onto a canvas with createImageBitmap (respects EXIF) + applies
// manual rotation, returns a normalized JPEG blob.
async function buildRotatedBlob(file: File | Blob, rotateDeg: number): Promise<Blob> {
  // imageOrientation: 'from-image' makes createImageBitmap honour EXIF —
  // without it Safari/Firefox return raw pixels and the canvas drops the
  // phone's portrait flag, so the manually-rotated blob ends up sideways.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const swap = rotateDeg === 90 || rotateDeg === 270;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotateDeg * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.85),
  );
}

export function ListImagePreviewRoute() {
  const { listId } = useParams({ from: '/lists/$listId/image-preview' });
  const { model } = useSearch({ from: '/lists/$listId/image-preview' });
  const navigate = useNavigate();
  const parseImage = useDebugParseShoppingListImage();

  const [file] = useState<File | null>(() => ocrImageStore.consume());
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const imgRef = useRef<HTMLImageElement>(null);

  const rotate = (dir: 1 | -1) =>
    setRotation((r) => ((r + dir * 90) % 360 + 360) % 360);

  const handleAnalyse = async () => {
    if (!file) return;
    const blob = rotation === 0 ? file : await buildRotatedBlob(file, rotation);
    parseImage.mutate(
      { file: blob, model: model as OcrModelId },
      {
        onSuccess: (result) => {
          ocrAnalyzeStore.set(result);
          void navigate({
            to: '/lists/$listId/image-analyze',
            params: { listId },
          });
        },
      },
    );
  };

  const back = () => void navigate({ to: '/lists/$listId', params: { listId } });

  const handleDebug = async () => {
    if (!file) return;
    const blob = rotation === 0 ? file : await buildRotatedBlob(file, rotation);
    ocrImageStore.set(blob instanceof File ? blob : new File([blob], 'image.jpg', { type: 'image/jpeg' }));
    void navigate({ to: '/lists/$listId/image-debug', params: { listId }, search: { model } });
  };

  return (
    <Screen>
      <XStack justifyContent="space-between" alignItems="center">
        <Button size="$2" chromeless onPress={back} disabled={parseImage.isPending}>
          ← Zurück
        </Button>
        <XStack gap="$2">
          <Button size="$3" onPress={() => rotate(-1)} disabled={parseImage.isPending || !file}>
            ⟲ 90°
          </Button>
          <Button size="$3" onPress={() => rotate(1)} disabled={parseImage.isPending || !file}>
            90° ⟳
          </Button>
        </XStack>
      </XStack>

      <YStack flex={1} justifyContent="center" alignItems="center" overflow="hidden">
        {objectUrl ? (
          <img
            ref={imgRef}
            src={objectUrl}
            alt="Vorschau"
            style={{
              maxWidth: '100%',
              maxHeight: '60vh',
              objectFit: 'contain',
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease',
              // When rotated 90/270 the image overflows its natural box;
              // swap width/height so the container keeps the right size.
              ...(rotation === 90 || rotation === 270
                ? { width: '60vh', height: '100%' }
                : {}),
            }}
          />
        ) : (
          <Paragraph opacity={0.5}>Kein Bild ausgewählt.</Paragraph>
        )}
      </YStack>

      {parseImage.isError ? (
        <Paragraph color="$red10" fontSize="$3">
          Fehler: {parseImage.error.message}
        </Paragraph>
      ) : null}

      <XStack gap="$2">
        <Button
          flex={1}
          chromeless
          disabled={parseImage.isPending || !file}
          onPress={() => void handleDebug()}
        >
          Debug
        </Button>
        <Button
          flex={3}
          theme="active"
          disabled={parseImage.isPending || !file}
          onPress={() => void handleAnalyse()}
        >
          {parseImage.isPending ? <Spinner size="small" /> : 'Analysieren'}
        </Button>
      </XStack>
    </Screen>
  );
}
